import type { CarryVestingStatus } from './carryFund';

export type FundMemberType = 'MEMBER' | 'DR_HOLDER';

export type FundMemberStatus = 'ACTIVE' | 'INACTIVE';

export type HoldingType = 'BV' | 'NV' | 'CV' | 'FGR' | 'COOPERATIEF';

export const HOLDING_TYPE_LABELS: Record<HoldingType, string> = {
  BV: 'BV (Besloten Vennootschap)',
  NV: 'NV (Naamloze Vennootschap)',
  CV: 'CV (Commanditaire Vennootschap)',
  FGR: 'FGR (Fonds voor Gemene Rekening)',
  COOPERATIEF: 'Coöperatief',
};

export type FundMemberPortalStatus =
  'NOT_INVITED' | 'GRANTED' | 'INVITED' | 'ACTIVE' | 'INACTIVE';

export const FUND_MEMBER_TYPE_LABELS: Record<FundMemberType, string> = {
  MEMBER: 'Member',
  DR_HOLDER: 'DR holder',
};

export const FUND_MEMBER_STATUS_LABELS: Record<FundMemberStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const FUND_MEMBER_STATUS_VARIANTS: Record<
  FundMemberStatus,
  'success' | 'secondary'
> = {
  ACTIVE: 'success',
  INACTIVE: 'secondary',
};

export const FUND_MEMBER_PORTAL_STATUS_LABELS: Record<
  FundMemberPortalStatus,
  string
> = {
  NOT_INVITED: 'Not invited',
  GRANTED: 'Access granted',
  INVITED: 'Invited',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const FUND_MEMBER_PORTAL_STATUS_VARIANTS: Record<
  FundMemberPortalStatus,
  'success' | 'secondary' | 'warning' | 'outline'
> = {
  NOT_INVITED: 'outline',
  GRANTED: 'warning',
  INVITED: 'warning',
  ACTIVE: 'success',
  INACTIVE: 'secondary',
};

export function holdingLabel(holdingName: string | null): string {
  return holdingName ?? 'In person';
}

export interface FundMember {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  member_type: FundMemberType;
  status: FundMemberStatus;
  notes: string | null;
  holding_name: string | null;
  holding_coc_number: string | null;
  holding_type: HoldingType | null;
  email: string | null;
  phone: string | null;
  portal_status: FundMemberPortalStatus;
  invited_at: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedFundMembers {
  items: FundMember[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface FundMemberOption {
  id: string;
  full_name: string;
}

export interface FundMemberPagination {
  page: number;
  page_size: number;
}

export interface FundMemberCreate {
  first_name: string;
  last_name: string;
  member_type: FundMemberType;
  holding_name?: string | null;
  holding_coc_number?: string | null;
  holding_type?: HoldingType | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  fund_ids?: string[];
  joined_on?: string | null;
}

export interface FundMemberUpdate {
  first_name?: string;
  last_name?: string;
  member_type?: FundMemberType;
  holding_name?: string | null;
  holding_coc_number?: string | null;
  holding_type?: HoldingType | null;
  email?: string | null;
  phone?: string | null;
  status?: FundMemberStatus;
  notes?: string | null;
}

export type FundMemberFundStatus = 'ACTIVE' | 'EXITED';

export const FUND_MEMBER_FUND_STATUS_LABELS: Record<
  FundMemberFundStatus,
  string
> = {
  ACTIVE: 'Active',
  EXITED: 'Exited',
};

export interface FundMemberFund {
  id: string;
  fund_id: string;
  fund_name: string;
  status: FundMemberFundStatus;
  joined_on: string | null;
  created_at: string;
  vesting_start_date: string | null;
  vesting_end_date: string | null;
  units: number;
  total_carry_units: number;
  vested_units: number;
  vesting_status: CarryVestingStatus;
  vested_days: number | null;
  unvested_days: number | null;
}

export interface FundMemberFundsCreate {
  fund_ids: string[];
  joined_on?: string | null;
}
